import {SchemaState} from "../SchemaState";
import {UpdateContainerMessage, UpdateSchemeMessage, visitUpdateSchemeMessage} from "../dto/com.rico.sb2.message";
import {SinglePopover} from "../lib/bootstrapPopover";
import {AliveListItem} from "./AliveListItem";
import {isAliveListWaiting, isAliveListWorking} from "./AliveListConstants";
import {PlayerDataAdapter, PlayerDataListener, UpdateSchemeMessageConsumer} from "../PlayerDataAdapter";
import {PlayerData_ContainerInfo} from "../dto/com.rico.sb2.service.positions";
import {createHtmlElement} from "../lib/domFunctions";
import {Messages} from "../messages/Messages";

const messages = new Messages();

export type AliveListMode = 'waiting' | 'working'

export class AliveList implements PlayerDataListener, UpdateSchemeMessageConsumer {
    readonly element: HTMLElement
    private readonly listElement: HTMLElement
    private readonly listEmpty: HTMLElement
    private readonly matches: (item: AliveListItem) => boolean

    readonly schemaState: SchemaState;
    readonly playerData: PlayerDataAdapter;

    private readonly items: Map<number, AliveListItem> = new Map()

    constructor(element: HTMLElement, schemaState: SchemaState, playerData: PlayerDataAdapter, mode: AliveListMode) {
        this.schemaState = schemaState
        this.playerData = playerData

        this.element = element
        this.listElement = this.element.querySelector('[data-bind="listAlive"]')!
        this.listEmpty = createHtmlElement('div', {class: 'text-muted fst-italic text-center p-3'}, `${messages.get('AliveList.emptyListMessage')}`)
        this.matches = mode == 'waiting'
            ? item => isAliveListWaiting(item.data)
            : item => isAliveListWorking(item.data)
        this.updateListEmptyNotice();
    }

    clear() {
        this.playerData.playerDataListeners.remove(this)
        this.playerData.updateListeners.remove(this)

        Array.from(this.items.keys()).forEach(id => this.removeContainer(id));

        this.listElement.innerHTML = ``;
        this.updateListEmptyNotice();
    }

    dispose() {
        this.clear();
        this.element.remove();
    }

    updateListEmptyNotice() {
        const items = Array.from(this.items.values())

        if (items.some(item => this.matches(item))) {
            this.listEmpty.remove();
        } else {
            this.listElement.append(this.listEmpty)
        }
    }

    reset() {
        this.clear();

        this.playerData.containers.map(c => this.addContainer(c))
        this.updateListEmptyNotice();

        this.playerData.playerDataListeners.add(this)
        this.playerData.updateListeners.add(this)
    }

    onContainerAdded(m: PlayerData_ContainerInfo): void {
        this.addContainer(m.id)
        this.updateListEmptyNotice();
    }

    onContainerRemoved(m: PlayerData_ContainerInfo): void {
        this.removeContainer(m.id);
        this.updateListEmptyNotice();
    }

    onContainerModify(m: PlayerData_ContainerInfo): void {
        this.updateContainer(m.id)
    }

    onSchemeUpdate(update: UpdateSchemeMessage): void {
        visitUpdateSchemeMessage(update, {
            containerMessage: m => this.onSchemeContainerUpdate(m)
        })
    }

    private onSchemeContainerUpdate(update: UpdateContainerMessage): void {
        this.updateContainer(update.id)
    }

    private updateContainer(id: number) {
        const item = this.items.get(id);
        if (!item) return

        item.update();
        this.updateItemList(item);
        this.updateListEmptyNotice();
    }

    private getListForWidget(item: AliveListItem): HTMLElement | null {
        return this.matches(item) ? this.listElement : null
    }

    private addContainer(id: number) {
        const widget = new AliveListItem(this, this.playerData.getContainerData(id));

        this.items.set(id, widget)
        this.updateItemList(widget)
        return widget
    }

    private removeContainer(id: number) {
        const widget = this.items.get(id);
        if (!widget) return;

        this.items.delete(id)
        SinglePopover.hideForElement(widget.element)
        widget.dispose()
    }

    private updateItemList(item: AliveListItem) {
        const newList = this.getListForWidget(item)
        if (newList == item.list) return

        item.element.remove()
        if (newList != null) {
            newList.appendChild(item.element)
        }
        item.list = newList
    }

    getLastTransportUpdate(transport: number | null) {
        return this.playerData.getTransportUpdate(transport)
    }

    getLastPositionUpdate(position: number | null) {
        return this.playerData.getPositionUpdate(position)
    }
}
